import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import useSWR from 'swr'
import { fetchSeries } from '../services/vodApi'
import { Button } from '../components'
import pageStyles from '../styles/page.module.scss'
import listTableStyles from '../styles/list-table.module.scss'
import buttonStyles from '../components/Button.module.scss'
import styles from './Series.module.scss'

const SeriesList: React.FC = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')

  const { data, isLoading, error, mutate } = useSWR('vod-series', fetchSeries, { revalidateOnFocus: false })

  const series = useMemo(() => {
    const all = data?.series || []
    const term = searchTerm.trim().toLowerCase()
    return term ? all.filter((s) => s.title.toLowerCase().includes(term)) : all
  }, [data, searchTerm])

  if (error) {
    return (
      <div className={pageStyles.pageContainer}>
        <div className={pageStyles.error}>
          <h2>Error loading series</h2>
          <p>{(error as Error).message}</p>
          <Button variant="danger" onClick={() => mutate()}>Retry</Button>
        </div>
      </div>
    )
  }

  return (
    <div className={pageStyles.pageContainer}>
      <div className={pageStyles.pageHeader}>
        <h1>Series</h1>
        <div className={pageStyles.headerButtons}>
          <Button onClick={() => navigate('/series/new')}>
            + New Series
          </Button>
        </div>
      </div>
      <p className={styles.muted}>Episodic shows. Assign a VOD to a season from the VOD&apos;s own page.</p>

      <div className={listTableStyles.searchSection}>
        <div className={listTableStyles.searchForm}>
          <div className={listTableStyles.searchInputGroup}>
            <input
              id="series-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search series by title..."
              className={listTableStyles.searchInput}
            />
            {searchTerm && (
              <button type="button" onClick={() => setSearchTerm('')} className={listTableStyles.clearButton}>
                Clear
              </button>
            )}
          </div>
        </div>
      </div>

      {isLoading && !data ? (
        <div className={pageStyles.loading}>
          <h3>Loading series...</h3>
        </div>
      ) : series.length === 0 ? (
        <div className={listTableStyles.noResults}>
          <h3>No series found</h3>
          <p>{searchTerm ? `No series match "${searchTerm.trim()}".` : 'No series have been created yet.'}</p>
        </div>
      ) : (
        <>
          <div className={`${listTableStyles.tableHeader} ${styles.listHeader}`}>
            <div>Title</div>
            <div>Description</div>
            <div>Seasons</div>
            <div>Actions</div>
          </div>
          <div className={listTableStyles.tableBody}>
            {series.map((s) => (
              <div key={s.tc_series_id} className={`${listTableStyles.tableRow} ${styles.listItem}`}>
                <div className={listTableStyles.cellPrimary}>
                  <p className={listTableStyles.primaryText}>{s.title}</p>
                </div>
                <div className={listTableStyles.cellText}>{s.description || '—'}</div>
                <div className={listTableStyles.cellText}>{s.seasons.length}</div>
                <div className={listTableStyles.cellActions}>
                  <Link
                    to={`/series/${s.tc_series_id}`}
                    className={`${buttonStyles.button} ${buttonStyles.primary} ${buttonStyles.medium}`}
                  >
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  )
}

export default SeriesList
